/**
 * Mounts every open tab's content; shows only the active one.
 * Keep-mounted (display:none) so list filters / form drafts survive switches.
 *
 * presentation:'modal' is a CSS wrapper around the SAME React node — never a
 * MUI Dialog portal (expand/collapse must not remount / lose typed input).
 */
import React, { Suspense, lazy, useCallback, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import { useTheme, alpha } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router-dom'
import { useConnection } from '@/app/contexts/ConnectionContext'
import { useAppWorkspaceStore } from './appWorkspaceStore'
import { parseTabPath } from './parseTabPath'
import { confirmCloseWorkspaceTab } from './confirmCloseWorkspaceTab'
import { pruneWorkspaceTabsMissingFrom } from './pruneWorkspaceTabs'
import { useKnownObjectNames, type KnownObjectNames } from './useKnownObjectNames'
import { RuntimeListBody } from './RuntimeListBody'
import { RuntimeFormBody } from './RuntimeFormBody'
import { PrintTabBody } from './PrintTabBody'
import { ConnectionMailPage } from '@/app/pages/ConnectionMailPage'
import { appRecordPath } from '@/runtime/entityRoutes'

function mountObjectBody(known: KnownObjectNames, objectName: string | undefined): boolean {
  if (!objectName) return false
  if (known.status === 'loading') return false
  if (known.status === 'unavailable') return true
  return known.names.has(objectName)
}

// Report bodies drag the whole report-builder (incl. recharts) — a cost only a
// user who actually opens a report tab should pay. Its own Suspense boundary,
// so a sibling tab is never unmounted while this chunk loads.
const ReportRunBody = lazy(() =>
  import('./ReportRunBody').then((m) => ({ default: m.ReportRunBody })),
)

export function WorkspaceHost() {
  const theme = useTheme()
  const tabs = useAppWorkspaceStore((s) => s.tabs)
  const activePath = useAppWorkspaceStore((s) => s.activePath)
  const { selectedConnection } = useConnection()
  const navigate = useNavigate()
  const location = useLocation()
  const connectionId = selectedConnection?.id
  const knownObjects = useKnownObjectNames(connectionId)

  const underlayPathRef = useRef<string | null>(null)
  const modalPanelRef = useRef<HTMLDivElement | null>(null)

  const activeTab = tabs.find((t) => t.path === activePath) ?? null
  const activePresentation = activeTab?.presentation ?? 'tab'

  // Remember the last full-area tab so modal/unresolved can sit over it.
  useEffect(() => {
    if (activeTab?.presentation === 'tab' && activePath) {
      underlayPathRef.current = activePath
    }
  }, [activePath, activeTab?.presentation])

  let underlayPath: string | null = null
  if (activePresentation === 'modal' || activePresentation === 'unresolved') {
    const remembered = underlayPathRef.current
    if (
      remembered &&
      remembered !== activePath &&
      tabs.some((t) => t.path === remembered && t.presentation === 'tab')
    ) {
      underlayPath = remembered
    } else {
      for (let i = tabs.length - 1; i >= 0; i -= 1) {
        const t = tabs[i]
        if (t.path !== activePath && t.presentation === 'tab') {
          underlayPath = t.path
          break
        }
      }
    }
  }

  const handleClosePath = useCallback(
    async (path: string) => {
      const next = await confirmCloseWorkspaceTab(path)
      if (next == null) return
      navigate(next)
    },
    [navigate],
  )

  // Escape closes the active modal (same path as ✕).
  useEffect(() => {
    if (activePresentation !== 'modal' || !activePath) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleClosePath(activePath)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activePresentation, activePath, handleClosePath])

  // Move focus into the modal panel when it becomes modal.
  useEffect(() => {
    if (activePresentation !== 'modal') return
    const id = window.requestAnimationFrame(() => {
      modalPanelRef.current?.focus()
    })
    return () => window.cancelAnimationFrame(id)
  }, [activePresentation, activePath])

  useEffect(() => {
    if (knownObjects.status !== 'ready') return
    const next = pruneWorkspaceTabsMissingFrom(
      knownObjects.names,
      `${location.pathname}${location.search || ''}`,
    )
    if (next) navigate(next)
  }, [knownObjects, location.pathname, location.search, navigate, tabs])

  if (!connectionId || tabs.length === 0) return null

  const modalZ = theme.zIndex.modal

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: activePath ? 'block' : 'none',
        overflow: 'auto',
      }}
    >
      {activePresentation === 'modal' && (
        <Box
          data-testid="workspace-modal-backdrop"
          aria-hidden
          onClick={() => activePath && handleClosePath(activePath)}
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: alpha(theme.palette.common.black, 0.45),
            zIndex: modalZ - 1,
          }}
        />
      )}

      {tabs.map((tab) => {
        const parsed = parseTabPath(tab.path)
        if (!parsed) return null

        const isModal = activePresentation === 'modal' && tab.path === activePath
        const isUnresolved =
          activePresentation === 'unresolved' && tab.path === activePath
        const isUnderlay =
          Boolean(underlayPath) &&
          tab.path === underlayPath &&
          (activePresentation === 'modal' || activePresentation === 'unresolved')
        const isFullTab =
          activePresentation === 'tab' &&
          tab.path === activePath &&
          tab.presentation === 'tab'

        const showAsSurface = isFullTab || isUnderlay || isModal || isUnresolved

        const resolvedPresentation =
          tab.presentation === 'modal' || tab.presentation === 'tab'
            ? tab.presentation
            : undefined

        return (
          <Box
            // Path changes on create Save (/new → /id); surfaceKey does not —
            // keying on path remounted the form and flashed a full reload.
            key={tab.surfaceKey}
            ref={isModal ? modalPanelRef : undefined}
            tabIndex={isModal ? -1 : undefined}
            role={isModal ? 'dialog' : undefined}
            aria-modal={isModal ? true : undefined}
            aria-label={isModal ? tab.title : undefined}
            aria-hidden={!showAsSurface || (isUnderlay && activePresentation === 'modal')}
            sx={
              isModal
                ? {
                    position: 'fixed',
                    zIndex: modalZ,
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'min(960px, 92vw)',
                    maxHeight: '90vh',
                    // User-resizable: a style change on the SAME element, so the
                    // expand/collapse remount contract above still holds.
                    resize: 'both',
                    minWidth: 420,
                    minHeight: 260,
                    maxWidth: '96vw',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 1,
                    outline: 'none',
                  }
                : isUnresolved
                  ? {
                      display: 'block',
                      visibility: 'hidden',
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      height: '100%',
                      overflow: 'hidden',
                    }
                  : {
                      display: isFullTab || isUnderlay ? 'block' : 'none',
                      height: '100%',
                    }
            }
          >
            {parsed.kind === 'list' && parsed.objectName && mountObjectBody(knownObjects, parsed.objectName) && (
              <RuntimeListBody
                connectionId={connectionId}
                objectName={parsed.objectName}
                formName={parsed.formName}
                tabPath={tab.path}
                onTitleChange={(title) =>
                  useAppWorkspaceStore.getState().setTitle(tab.path, title)
                }
              />
            )}
            {parsed.kind === 'record' && parsed.objectName && mountObjectBody(knownObjects, parsed.objectName) && (
              <RuntimeFormBody
                connectionId={connectionId}
                objectName={parsed.objectName}
                recordId={parsed.recordId ?? null}
                formName={parsed.formName}
                tabPath={tab.path}
                presentation={resolvedPresentation}
                onClose={() => handleClosePath(tab.path)}
                onSaved={(record, opts) => {
                  // Save-and-close: skip rename+navigate. navigate(/id) while
                  // onClose runs races WorkspaceRoute clearUserClosed+openTab
                  // and resurrects the tab (first click only saved).
                  if (opts?.willClose) return
                  if (parsed.recordId == null && record?.id) {
                    const id = String(record.id)
                    const oldPath = tab.path
                    const newPath = parsed.formName
                      ? `/app/${parsed.objectName}/${id}?form=${encodeURIComponent(parsed.formName)}`
                      : appRecordPath(parsed.objectName!, id)
                    // Path updates; surfaceKey stays — form stays mounted.
                    useAppWorkspaceStore.getState().replacePath(oldPath, newPath)
                    navigate(newPath, { replace: true })
                  }
                }}
                onTitleChange={(title) =>
                  useAppWorkspaceStore.getState().setTitle(tab.path, title)
                }
                onStatusChange={(status) =>
                  useAppWorkspaceStore.getState().setStatus(tab.path, status)
                }
                onDirtyChange={(dirty) =>
                  useAppWorkspaceStore.getState().setDirty(tab.path, dirty)
                }
                onTogglePresentation={() => {
                  const current = useAppWorkspaceStore
                    .getState()
                    .tabs.find((t) => t.path === tab.path)?.presentation
                  if (current === 'modal') {
                    useAppWorkspaceStore.getState().setPresentation(tab.path, 'tab')
                  } else if (current === 'tab') {
                    useAppWorkspaceStore.getState().setPresentation(tab.path, 'modal')
                  }
                }}
              />
            )}
            {parsed.kind === 'report' && parsed.reportId && (
              <Suspense fallback={null}>
                <ReportRunBody
                  connectionId={connectionId}
                  reportId={parsed.reportId}
                  tabPath={tab.path}
                  onTitleChange={(title) =>
                    useAppWorkspaceStore.getState().setTitle(tab.path, title)
                  }
                />
              </Suspense>
            )}
            {parsed.kind === 'print' && parsed.sessionId && (
              <PrintTabBody
                sessionId={parsed.sessionId}
                onTitleChange={(title) =>
                  useAppWorkspaceStore.getState().setTitle(tab.path, title)
                }
              />
            )}
            {parsed.kind === 'mail' && <ConnectionMailPage />}
          </Box>
        )
      })}
    </Box>
  )
}
